import { z } from 'zod';
import { CORE_ENTITIES } from './entities.js';
import { KEY_REGEX } from './fields.js';
export const Expr = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(Expr),
    z.object({ $path: z.string() }),
    z.object({ $template: z.string() }),
    z.object({ $lookup: z.object({ key: Expr, table: z.record(z.string(), z.unknown()), default: Expr.optional() }) }),
    z.object({ $concat: z.array(Expr) }),
    z.object({ $add: z.array(Expr) }),
    z.object({ $subtract: z.tuple([Expr, Expr]) }),
    z.object({ $multiply: z.array(Expr) }),
    z.object({ $divide: z.tuple([Expr, Expr]) }),
    z.object({ $abs: Expr }),
    z.object({ $round: z.tuple([Expr, z.number().optional()]) }),
    z.object({ $coalesce: z.array(Expr) }),
    z.object({ $if: z.object({ condition: Condition, then: Expr, else: Expr.optional() }) }),
    z.object({ $now: z.literal(true) }),
    z.object({ $today: z.literal(true) }),
    z.object({ $date_add: z.tuple([Expr, z.number()]) }),
    z.object({ $len: Expr }),
    z.object({ $first: Expr }),
    z.object({ $var: z.string() }),
    z.record(z.string(), Expr),
]));
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
export const CONDITION_OPS = [
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'not_in',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'matches',
    'is_set',
    'is_empty',
    'between',
    'within_days',
    'before',
    'after',
    'changed',
];
export const Condition = z.lazy(() => z.union([
    z.object({ all: z.array(Condition).min(1) }),
    z.object({ any: z.array(Condition).min(1) }),
    z.object({ not: Condition }),
    z.object({ path: z.string().min(1), op: z.enum(CONDITION_OPS), value: Expr.optional() }),
]));
// ---------------------------------------------------------------------------
// Entity references inside steps
// ---------------------------------------------------------------------------
export const EntityRef = z.union([
    z.literal('$entity'), // the event's primary entity
    z.object({ entity: z.enum(CORE_ENTITIES), id: Expr }),
]);
// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------
const varName = z.string().regex(/^[a-z][a-z0-9_]{0,40}$/);
export const FindFilter = z.object({
    field: z.string(),
    op: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'contains', 'is_set', 'is_empty', 'between', 'within_days']),
    value: Expr.optional(),
});
export const Step = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('set_field'),
        target: EntityRef.default('$entity'),
        field: z.string().min(1), // column name or "fields.<key>"
        value: Expr,
    }),
    z.object({
        type: z.literal('set_status'),
        target: EntityRef.default('$entity'),
        status: z.string().min(1),
        reason: Expr.optional(),
    }),
    z.object({
        type: z.literal('apply_discount'),
        target: EntityRef.default('$entity'), // invoices | quotes | jobs
        percent: Expr,
        reason: Expr.optional(),
    }),
    z.object({
        type: z.literal('add_line'),
        target: EntityRef.default('$entity'),
        kind: z.enum(['service', 'discount', 'fee', 'adjustment']).default('service'),
        description: Expr,
        quantity: Expr.default(1),
        unit_price: Expr,
        tax_rate: Expr.optional(),
        service_id: Expr.optional(),
    }),
    z.object({
        type: z.literal('create_record'),
        entity: z.enum(CORE_ENTITIES),
        data: z.record(z.string(), Expr),
        as: varName.optional(),
    }),
    z.object({
        type: z.literal('link'),
        link_key: z.string().regex(KEY_REGEX),
        from: EntityRef,
        to: EntityRef,
    }),
    z.object({
        type: z.literal('attach_document'),
        document_id: Expr,
        to: EntityRef,
    }),
    z.object({
        type: z.literal('match_transaction'),
        transaction_id: Expr,
        entity: z.enum(['payments', 'expenses', 'invoices', 'documents']),
        entity_id: Expr,
        amount: Expr.optional(),
        confidence: Expr.optional(),
        note: Expr.optional(),
    }),
    z.object({
        type: z.literal('find'),
        entity: z.enum(CORE_ENTITIES),
        where: z.array(FindFilter).default([]),
        order_by: z.string().optional(),
        limit: z.number().int().min(1).max(50).default(10),
        as: varName,
    }),
    z.object({
        type: z.literal('llm_extract'),
        from: Expr, // text, or {"document_id": "..."} to read an attachment
        schema: z.record(z.string(), z.object({ type: z.enum(['string', 'number', 'boolean', 'date']), description: z.string().optional() })),
        instructions: z.string().optional(),
        as: varName,
    }),
    z.object({
        type: z.literal('llm_classify'),
        input: Expr,
        classes: z.array(z.object({ value: z.string(), description: z.string().optional() })).min(2),
        instructions: z.string().optional(),
        as: varName,
    }),
    z.object({
        type: z.literal('llm_match'),
        target: Expr,
        candidates: Expr,
        instructions: z.string().optional(),
        as: varName, // -> { index: number|null, candidate, confidence, reasoning }
    }),
    z.object({
        type: z.literal('notify'),
        channel: z.enum(['inbox', 'email']).default('inbox'),
        to: Expr.optional(),
        subject: Expr,
        body: Expr,
    }),
    z.object({
        type: z.literal('stop_if'),
        condition: Condition,
        reason: z.string().optional(),
    }),
]);
// ---------------------------------------------------------------------------
// Trigger + rule
// ---------------------------------------------------------------------------
export const Trigger = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('event'), event: z.string().min(3) }),
    z.object({ kind: z.literal('schedule'), cron: z.string().min(9), entity: z.enum(CORE_ENTITIES).optional(), where: z.array(FindFilter).optional() }),
]);
export const RuleInput = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,60}$/).optional(),
    name: z.string().min(1).max(120),
    description: z
        .string()
        .max(1000)
        .optional()
        .describe('One sentence in the business owner\'s own words explaining what this rule is for and why. Shown wherever the rule appears, so write it for a colleague who did not set it up.'),
    enabled: z.boolean().default(true),
    trigger: Trigger,
    condition: Condition.optional(),
    steps: z.array(Step).min(1).max(20),
    mode: z.enum(['auto', 'propose']).default('propose'),
    auto_apply_threshold: z.number().min(0).max(1).optional(),
});
/** Steps whose results can only be trusted with a confidence check. */
export const FUZZY_STEP_TYPES = new Set(['llm_extract', 'llm_classify', 'llm_match']);
export function ruleIsFuzzy(rule) {
    return rule.steps.some((s) => FUZZY_STEP_TYPES.has(s.type));
}
/** Documentation embedded in tool descriptions so the agent can author rules correctly. */
export const RULE_DSL_GUIDE = `
RULE FORMAT
  trigger:   {"kind":"event","event":"invoice.created"}  or  {"kind":"schedule","cron":"0 8 * * 1"}
             Events: <entity>.created|updated|deleted, <entity>.<status> (invoice.sent, job.completed, quote.accepted),
             email.received, bank_transaction.created. Wildcards allowed: "invoice.*".
  condition: optional. {"all":[…]} | {"any":[…]} | {"not":…} | {"path":"customer.fields.discount_tier","op":"is_set"}
             ops: eq neq gt gte lt lte in not_in contains starts_with ends_with matches is_set is_empty between within_days before after changed
  steps:     ordered list. Each step runs with the context below; "as" names store results as variables.
  mode:      "auto" applies immediately. "propose" lands in the review queue for a human. Fuzzy (llm_*) rules should propose
             unless auto_apply_threshold is set (e.g. 0.9) — then only high-confidence results auto-apply.

CONTEXT PATHS (use in "$path", conditions and "{{templates}}")
  event.type, event.payload.changed         entity.<column>, entity.fields.<key>    (the record that fired the event)
  invoice / job / quote / customer / payment / expense / bank_transaction / appointment  (same record by name, plus related rows)
  customer.display_name, customer.fields.<key>, job.title, vendor.name, tenant.currency, tenant.settings.<key>
  email.from, email.subject, email.body_text, email.attachments[].document_id   (email.received only)
  <var> for anything stored with "as"

EXPRESSIONS
  literal | {"$path":"customer.fields.discount_tier"} | {"$template":"{{customer.display_name}} discount"}
  {"$lookup":{"key":{"$path":"…"},"table":{"gold":15,"silver":10},"default":0}}
  {"$add":[…]} {"$multiply":[…]} {"$subtract":[a,b]} {"$round":[x,2]} {"$coalesce":[…]} {"$if":{"condition":…,"then":…,"else":…}}
  {"$now":true} {"$today":true} {"$date_add":[{"$today":true},14]} {"$len":…} {"$first":…}

STEPS
  set_field        {target?, field:"fields.<key>"|"<column>", value}
  set_status       {target?, status, reason?}
  apply_discount   {target?, percent, reason?}                     invoices/quotes/jobs
  add_line         {target?, kind?, description, quantity?, unit_price, tax_rate?}
  create_record    {entity, data:{…}, as?}
  link             {link_key, from, to}                            from/to = "$entity" or {entity, id}
  attach_document  {document_id, to}
  match_transaction{transaction_id, entity, entity_id, amount?, confidence?, note?}
  find             {entity, where:[{field,op,value}], order_by?, limit?, as}      deterministic search
  llm_extract      {from, schema:{field:{type,description}}, instructions?, as}   from = text or {"document_id":"…"}
  llm_classify     {input, classes:[{value,description}], as}       result: {value, confidence}
  llm_match        {target, candidates, instructions?, as}          result: {index, candidate, confidence, reasoning}
  notify           {channel:"inbox"|"email", to?, subject, body}
  stop_if          {condition, reason?}                             halt quietly when condition is true
`.trim();
//# sourceMappingURL=rules.js.map